import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@shared/enums/permission.enum';
import { UserResponse } from '../../user.model';
import { UserStore } from '../../user.store';
import {SHARED_IMPORTS} from "../../../../shared/modules/shared-imports.module";
import { SHARED_UTILS } from '@shared/modules/shared-utils.module';
import { UserTable, UsersPageChangedEvent } from '../../components/user-table/user-table.component';

@Component({
  selector: 'app-user-management-list-page',
  imports: [...SharedMaterialUIModule, ...SHARED_IMPORTS, UserTable],
  templateUrl: './user-management-list.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserManagementListPage {
  protected readonly userStore = inject(UserStore);
  private readonly permissionService = inject(PermissionService);

  protected readonly actionUserId = signal<string | null>(null);
  protected readonly utils = SHARED_UTILS();

  constructor() {
    this.userStore.loadUsers();
  }

  protected canCreate(): boolean {
    return this.permissionService.hasPermission(
      Permission.UsersManagementCreate
    );
  }

  protected canDeactivate(): boolean {
    return this.permissionService.hasPermission(
      Permission.UsersManagementDelete
    );
  }

  protected canActivate(): boolean {
    return this.permissionService.hasPermission(
      Permission.UsersManagementUpdate
    );
  }

  protected refresh(): void {
    this.userStore.loadUsers();
  }

  protected searchUsers(search: string): void {
    this.userStore.loadUsers(
      {
        pageNumber: 1,
        pageSize: this.userStore.usersPageSize(),
      },
      search,
    );
  }

  protected changePage(event: UsersPageChangedEvent): void {
    this.userStore.loadUsers({
      pageNumber: event.pageNumber,
      pageSize: event.pageSize,
    });
  }

  protected toggleUserActive(user: UserResponse): void {
    this.actionUserId.set(user.id);

    const complete = () => this.actionUserId.set(null);

    if (user.isActive) {
      if (!this.canDeactivate()) {
        this.actionUserId.set(null);
        return;
      }

      this.userStore.deactivateUser(user.id, complete);
      return;
    }

    if (!this.canActivate()) {
      this.actionUserId.set(null);
      return;
    }

    this.userStore.activateUser(user.id, complete);
  }
}
